if (!customElements.get('wlsn-header')) {
  class WLSNHeader extends HTMLElement {
    constructor() {
      super();

      this.classes = {
        active: 'active',
        hidden: 'wlsn-header--hidden',
        sticky: 'wlsn-header--sticky',
        stickyScrollUp: 'wlsn-header--sticky-scroll-up',
        section: 'wlsn-header-section',
        headerMenuWrapper: 'js-header-menu-wrapper',
        lockScroll: 'wlsn-lock-scroll',
        mobileMenuToggle: 'js-mobile-menu-toggle',
        megaMenuOverlay: 'js-wlsn-megamenu-dropdown-overlay',
        wlsnAccordionElement: 'wlsn-accordion',
        stickyActiveClass: 'wlsn-header--sticky-active',
        dropdownOverlay: 'wlsn-menu-dropdown__overlay',
        dropdownForceClose: 'wlsn-dropdown--force-close',
      };

      this.behavior = this.dataset.headerBehavior || '';
      this.lastScroll = window.scrollY;
      this.isMobileMenuOpen = false;
      this.stickyScrollThreshold = 100; // Minimum scroll distance before stickyActiveClass is added
      /** @type {(() => void) | null} */
      this._boundSyncBottomOffset = null;
      /** @type {ResizeObserver | null} */
      this._hostBottomResizeObserver = null;

      // Store bound methods for proper event listener cleanup
      this._boundHandleScroll = this._handleScroll.bind(this);
      this._boundHandleStickyActive = this._handleStickyActive.bind(this);
      this._boundOnEscKey = this._onEscKey.bind(this);
      this._boundOnDropdownOverlayClick = this._onDropdownOverlayClick.bind(this);
    }

    /**
     * Get DOM elements
     * @returns {object} - Object containing DOM element references
     */
    get elements() {
      return {
        headerSection: document.querySelector(`.${this.classes.section}`),
        headerMenuWrapper: this.querySelector(`.${this.classes.headerMenuWrapper}`),
        mobileMenuToggles: this.querySelectorAll(`.${this.classes.mobileMenuToggle}`),
        megaMenuOverlay: this.querySelector(`.${this.classes.megaMenuOverlay}`),
      };
    }

    /**
     * Connected callback lifecycle method
     * @returns {void}
     */
    connectedCallback() {
      const { megaMenuOverlay, mobileMenuToggles, headerSection } = this.elements;

      if (megaMenuOverlay) {
        megaMenuOverlay.addEventListener('click', this._onMegaMenuOverlayClick.bind(this));
      }

      if (window.innerWidth < 1023) {
        mobileMenuToggles.forEach((toggle) => {
          toggle.addEventListener('click', this._mobileMenuToggle.bind(this));
        });
      }

      if (window.innerWidth >= 1024) {
        this.querySelectorAll(`.${this.classes.dropdownOverlay}`).forEach((overlay) => {
          overlay.addEventListener('click', this._boundOnDropdownOverlayClick);
        });
      }

      if (headerSection) {
        headerSection.classList.remove(this.classes.sticky, this.classes.stickyScrollUp);
        this._handleHeaderBehavior();
      }
      this._setHeaderHeight();

      this._boundSyncBottomOffset = () => this._syncBottomOffsetFromHost();
      window.addEventListener('resize', this._boundSyncBottomOffset);
      this._hostBottomResizeObserver = new ResizeObserver(() => this._syncBottomOffsetFromHost());
      this._hostBottomResizeObserver.observe(this);
      this._scheduleSyncBottomOffset();

      // ESC key handle for all modals
      document.addEventListener('keydown', this._boundOnEscKey);
    }

    /**
     * Disconnected callback lifecycle method
     * @returns {void}
     */
    disconnectedCallback() {
      const { megaMenuOverlay, mobileMenuToggles, headerSection } = this.elements;

      if (megaMenuOverlay) {
        megaMenuOverlay.removeEventListener('click', this._onMegaMenuOverlayClick.bind(this));
      }

      if (window.innerWidth < 1023) {
        mobileMenuToggles.forEach((toggle) => {
          toggle.removeEventListener('click', this._mobileMenuToggle.bind(this));
        });
      }

      this.querySelectorAll(`.${this.classes.dropdownOverlay}`).forEach((overlay) => {
        overlay.removeEventListener('click', this._boundOnDropdownOverlayClick);
      });

      if (headerSection) {
        headerSection.classList.remove(this.classes.sticky, this.classes.stickyScrollUp, this.classes.stickyActiveClass);
      }

      if (this.behavior === 'sticky_scroll_up' || this.behavior === 'always_sticky') {
        window.removeEventListener('scroll', this._boundHandleStickyActive);
      }
      if (this.behavior === 'sticky_scroll_up') {
        window.removeEventListener('scroll', this._boundHandleScroll);
      }
      if (this._boundSyncBottomOffset) {
        window.removeEventListener('resize', this._boundSyncBottomOffset);
        this._boundSyncBottomOffset = null;
      }
      if (this._hostBottomResizeObserver) {
        this._hostBottomResizeObserver.disconnect();
        this._hostBottomResizeObserver = null;
      }
      document.removeEventListener('keydown', this._boundOnEscKey);
    }

    /**
     * Reset accordion state by closing all accordions
     * @returns {void}
     */
    _resetAccordionState() {
      const { headerMenuWrapper } = this.elements;
      if (headerMenuWrapper) {
        headerMenuWrapper.querySelectorAll(`${this.classes.wlsnAccordionElement}`).forEach((acc) => {
          acc.dispatchEvent(new CustomEvent('wlsn:accordion:close-all', { bubbles: false }));
        });
      }
    }

    /**
     * Handle dropdown overlay click on desktop — force-closes the dropdown
     * and removes the override once the cursor leaves the accordion.
     * @param {MouseEvent} e
     * @returns {void}
     */
    _onDropdownOverlayClick(e) {
      const accordion = e.currentTarget.closest(this.classes.wlsnAccordionElement);
      if (!accordion) return;
      accordion.classList.add(this.classes.dropdownForceClose);
      accordion.addEventListener(
        'mouseleave',
        () => accordion.classList.remove(this.classes.dropdownForceClose),
        { once: true }
      );
    }

    /**
     * Handle mega menu overlay click event
     * @returns {void}
     */
    _onMegaMenuOverlayClick() {
      const { headerMenuWrapper } = this.elements;
      if (headerMenuWrapper?.classList.contains(this.classes.active)) {
        this._resetMobileMenuState();
      }
    }

    /**
     * Toggle mobile menu open/closed
     * @returns {void}
     */
    _mobileMenuToggle() {
      const { headerMenuWrapper, megaMenuOverlay, mobileMenuToggles } = this.elements;
      if (headerMenuWrapper?.classList.contains(this.classes.active)) {
        this._resetMobileMenuState();
      } else {
        headerMenuWrapper?.classList.add(this.classes.active);
        megaMenuOverlay?.classList.add(this.classes.active);
        mobileMenuToggles.forEach((toggle) => toggle.classList.add(this.classes.active));
        this._lockScroll();
        this._updateOverlayState();
        this.isMobileMenuOpen = true;
      }
    }

    /**
     * Update overlay state based on menu state
     * @returns {void}
     */
    _updateOverlayState() {
      const { megaMenuOverlay, headerMenuWrapper } = this.elements;
      if (megaMenuOverlay) {
        megaMenuOverlay.classList.toggle(
          this.classes.active,
          headerMenuWrapper.classList.contains(this.classes.active)
        );
      }
    }

    /**
     * Reset mobile menu to closed state
     * @returns {void}
     */
    _resetMobileMenuState() {
      const { headerMenuWrapper, megaMenuOverlay, mobileMenuToggles } = this.elements;
      headerMenuWrapper?.classList.remove(this.classes.active);
      megaMenuOverlay?.classList.remove(this.classes.active);
      mobileMenuToggles.forEach((toggle) => toggle.classList.remove(this.classes.active));
      this._unlockScroll();
      this._resetAccordionState();
      this._updateOverlayState();
      this.isMobileMenuOpen = false;
    }

    /**
     * Handle ESC key press
     * @param {KeyboardEvent} e - The keyboard event
     * @returns {void}
     */
    _onEscKey(e) {
      if (e.key === 'Escape') {
        if (this.isMobileMenuOpen) {
          this._resetMobileMenuState();
        }
      }
    }

    /**
     * Handle header behavior based on settings
     * @returns {void}
     */
    _handleHeaderBehavior() {
      const { headerSection } = this.elements;
      if (this.behavior === 'always_sticky') {
        headerSection?.classList.add(this.classes.sticky);
        window.addEventListener('scroll', this._boundHandleStickyActive);
        // Check initial scroll position
        this._handleStickyActive();
      } else if (this.behavior === 'sticky_scroll_up') {
        headerSection?.classList.add(this.classes.stickyScrollUp);
        window.addEventListener('scroll', this._boundHandleScroll);
        window.addEventListener('scroll', this._boundHandleStickyActive);
        // Check initial scroll position
        this._handleStickyActive();
      }
    }

    /**
     * Handle scroll event for sticky header
     * @returns {void}
     */
    _handleScroll() {
      const { headerSection } = this.elements;
      const currentScroll = window.scrollY;
      if (!headerSection) return;
      headerSection.classList.toggle(
        this.classes.hidden,
        currentScroll > this.lastScroll && currentScroll > 20
      );
      this.lastScroll = currentScroll;
      // 300ms delay because transition for transform is 0.2s in css
      setTimeout(() => {
        this._scheduleSyncBottomOffset();
      }, 300);
    }

    /**
     * Handle sticky active class based on scroll position with threshold
     * @returns {void}
     */
    _handleStickyActive() {
      const { headerSection } = this.elements;
      if (!headerSection) return;
      
      const currentScroll = window.scrollY;
      const shouldBeActive = currentScroll > this.stickyScrollThreshold;
      
      headerSection.classList.toggle(this.classes.stickyActiveClass, shouldBeActive);
      // 300ms delay because transition for transform is 0.2s in css
      setTimeout(() => {
        this._scheduleSyncBottomOffset();
      }, 300);
    }

    /**
     * Distance from the top of the viewport to the bottom edge of the header,
     * exposed as `--wlsn-header-bottom-offset` in rem.
     * @returns {void}
     */
    _syncBottomOffsetFromHost() {
      const rootFontPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const bottom = Math.max(0, this.getBoundingClientRect().bottom);
      const offsetRem = Math.round((bottom / rootFontPx) * 100) / 100;
      this.style.setProperty('--wlsn-header-bottom-offset', `${offsetRem}rem`);
    }

    /**
     * @returns {void}
     */
    _scheduleSyncBottomOffset() {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          this._syncBottomOffsetFromHost();
        });
      });
    }

    /**
     * Set header height CSS variable
     * @returns {void}
     */
    _setHeaderHeight() {
      const headerHeight = this.offsetHeight;
      const rootFontSize = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
      const headerHeightRem = headerHeight / rootFontSize;
      document.documentElement.style.setProperty('--wlsn-header-height', `${headerHeightRem}rem`);
    }

    /**
     * Lock body scroll
     * @returns {void}
     */
    _lockScroll() {
      document.body.classList.add(this.classes.lockScroll);
    }

    /**
     * Unlock body scroll
     * @returns {void}
     */
    _unlockScroll() {
      document.body.classList.remove(this.classes.lockScroll);
    }
  }
  customElements.define('wlsn-header', WLSNHeader);
}
